import { useResilientStorage } from '@ctw/shared/hooks/use-resilient-storage';
import { writeResilientStorage } from '@ctw/shared/utils/resilient-storage-provider';
import { useCallback, useMemo } from 'react';

// Placeholder key segment used while no report is selected. toggleExcluded is
// gated on a defined jobId, so nothing is ever written under this key.
const NO_JOB_SENTINEL = '__no-job__';

const STORAGE_KEY_PREFIX = 'zus:atlas:excluded-fields:';

export const excludedFieldsStorageKey = (upid: string, jobId: string | undefined): string =>
  `${STORAGE_KEY_PREFIX}${upid}:${jobId ?? NO_JOB_SENTINEL}`;

/**
 * Removes this patient's persisted exclusions for extraction jobs that no
 * longer exist (jobs expire server-side), so entries don't accumulate forever.
 */
export const pruneExcludedFieldsStorage = (upid: string, activeJobIds: Array<string>): void => {
  let keys: Array<string>;
  try {
    keys = Object.keys(window.localStorage);
  } catch {
    return;
  }
  const patientPrefix = `${STORAGE_KEY_PREFIX}${upid}:`;
  const active = new Set(activeJobIds);
  for (const key of keys) {
    if (key.startsWith(patientPrefix) && !active.has(key.slice(patientPrefix.length))) {
      writeResilientStorage('local', key, undefined);
    }
  }
};

export interface ExcludedFieldsState {
  excludedIds: Array<string>;
  isExcluded: (fieldId: string) => boolean;
  toggleExcluded: (fieldId: string) => void;
}

/**
 * Which Atlas fields the user has excluded from export, persisted per
 * patient + extraction job.
 *
 * The underlying setter derives "previous" from the render-captured snapshot,
 * so callers must not toggle more than once per render (any future bulk action
 * must batch into a single set).
 */
export const useExcludedFields = (upid: string, jobId: string | undefined): ExcludedFieldsState => {
  const [storedIds, setStoredIds] = useResilientStorage<Array<string>>(
    excludedFieldsStorageKey(upid, jobId),
    [],
    { mode: 'local' },
  );

  // Guards the no-job state and corrupt values (valid JSON of the wrong shape).
  const excludedIds = useMemo(
    () => (jobId !== undefined && Array.isArray(storedIds) ? storedIds : []),
    [jobId, storedIds],
  );

  const isExcluded = useCallback((fieldId: string) => excludedIds.includes(fieldId), [excludedIds]);

  const toggleExcluded = useCallback(
    (fieldId: string) => {
      if (jobId === undefined) {
        // There's no Atlas report loaded
        return;
      }
      setStoredIds((previous) => {
        const current = Array.isArray(previous) ? previous : [];
        return current.includes(fieldId)
          ? current.filter((id) => id !== fieldId)
          : [...current, fieldId];
      });
    },
    [jobId, setStoredIds],
  );

  return { excludedIds, isExcluded, toggleExcluded };
};
